import { useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const specialties = [
  'General Physician', 'Dentist', 'Cardiologist', 'Dermatologist',
  'Orthopedic', 'Gynecologist', 'Ophthalmologist', 'ENT Specialist',
  'Pediatrician', 'Neurologist', 'Oncologist', 'Urologist',
  'Gastroenterologist', 'Plastic Surgeon', 'IVF / Fertility', 'Other',
];

const clinicSizes = [
  'Solo Practice', '2-5 Doctors', '6-15 Doctors', '16-50 Doctors', '50+ Doctors / Hospital',
];

const budgets = [
  '$300 - $400/mo', '$400 - $600/mo', '$600 - $800/mo', '$800+/mo',
];

const goals = [
  'Increase Patient Footfall', 'Build Online Presence', 'Get an AI-Powered Website',
  'Improve Google & SEO Ranking', 'Social Media Growth', 'All of the Above',
];

const initForm = { name: '', email: '', phone: '', clinic_name: '', specialty: '', clinic_size: '', budget: '', primary_goal: '', message: '' };

const ConsultationForm = () => {
  const [form, setForm] = useState(initForm);
  const [loading, setLoading] = useState(false);

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.clinic_name || !form.specialty || !form.clinic_size || !form.budget || !form.primary_goal) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/consultations`, form);
      toast.success('Consultation request submitted! We will get back to you shortly.');
      setForm(initForm);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="consultation" className="bg-[#F9F7F2] py-20 lg:py-28" data-testid="consultation-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8C6330] mb-4">Get Started</p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#11181C]">
            Book a Free Strategy Session
          </h2>
          <p className="text-base text-[#4A5568] mt-3 max-w-xl mx-auto">
            Tell us about your practice and we'll create a customized growth plan for you.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[#D6CEBF] rounded-2xl p-8 lg:p-10 shadow-sm" data-testid="consultation-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Full Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="Dr. John Doe"
                className="form-input-underline"
                data-testid="form-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="doctor@clinic.com"
                className="form-input-underline"
                data-testid="form-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Phone *</Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="Your phone number"
                className="form-input-underline"
                data-testid="form-phone"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Clinic Name *</Label>
              <Input
                value={form.clinic_name}
                onChange={(e) => update('clinic_name', e.target.value)}
                placeholder="Your Clinic Name"
                className="form-input-underline"
                data-testid="form-clinic-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Specialty *</Label>
              <Select value={form.specialty} onValueChange={(v) => update('specialty', v)}>
                <SelectTrigger className="form-input-underline" data-testid="form-specialty">
                  <SelectValue placeholder="Select your specialty" />
                </SelectTrigger>
                <SelectContent>
                  {specialties.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Clinic Size *</Label>
              <Select value={form.clinic_size} onValueChange={(v) => update('clinic_size', v)}>
                <SelectTrigger className="form-input-underline" data-testid="form-clinic-size">
                  <SelectValue placeholder="Select clinic size" />
                </SelectTrigger>
                <SelectContent>
                  {clinicSizes.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Monthly Budget *</Label>
              <Select value={form.budget} onValueChange={(v) => update('budget', v)}>
                <SelectTrigger className="form-input-underline" data-testid="form-budget">
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent>
                  {budgets.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Primary Goal *</Label>
              <Select value={form.primary_goal} onValueChange={(v) => update('primary_goal', v)}>
                <SelectTrigger className="form-input-underline" data-testid="form-goal">
                  <SelectValue placeholder="Select primary goal" />
                </SelectTrigger>
                <SelectContent>
                  {goals.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mb-8">
            <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-2 block">Additional Message</Label>
            <Textarea
              value={form.message}
              onChange={(e) => update('message', e.target.value)}
              placeholder="Tell us more about your practice and goals..."
              rows={4}
              className="form-input-underline resize-none"
              data-testid="form-message"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1B3F33] text-white hover:bg-[#265E4A] rounded-full px-8 py-4 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="form-submit"
          >
            {loading ? 'Submitting...' : 'Book a Free Consultation'}
            {!loading && <Send size={16} />}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ConsultationForm;
